import { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardPreview,
  Content,
  Heading,
  FileTrigger,
  DropZone,
  LabeledValue,
  ProgressCircle,
} from "@react-spectrum/s2";
import { Button as AriaButton } from "react-aria-components";
import UploadToCloudIcon from "@react-spectrum/s2/icons/UploadToCloud";
import ImageIcon from "@react-spectrum/s2/illustrations/linear/Image";

import { S3Image } from "@app/components/S3Image";
import { HoverOverlay } from "@app/components/HoverOverlay";
import { ThumbnailCaption } from "@app/components/ThumbnailCaption";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

/**
 * A `DropZone`'s `getDropOperation` accepting only images — dropped files as
 * well as the library and scraped images dragged from the wizard's sidebar.
 */
export const getImageDragOperation = ({ types }) =>
  Array.from(types)[0]?.startsWith("image/") ? "copy" : "cancel";

/**
 * The `UNSAFE_style` of an image's `DropZone`: its dashed border hugs the
 * preview. Shared so every droppable image thumbnail looks the same.
 */
export const IMAGE_DROP_ZONE_STYLE = {
  padding: 4,
  borderRadius: "1rem",
};

/**
 * How far an image drop zone's dashed border sits outside the preview it
 * wraps: its padding plus the S2 `DropZone`'s 2px border.
 */
export const IMAGE_DROP_ZONE_OUTSET = IMAGE_DROP_ZONE_STYLE.padding + 2;

/**
 * What fades over an image thumbnail while the user hovers it, to say that a
 * click picks a file for it (the one of `TemplateAssets`' images). `compact`
 * is for small thumbnails.
 */
export const UploadHoverOverlay = ({ isHovered, compact = false }) => (
  <HoverOverlay
    isHovered={isHovered}
    icon={UploadToCloudIcon}
    compact={compact}
  />
);

UploadHoverOverlay.propTypes = {
  isHovered: PropTypes.bool,
  compact: PropTypes.bool,
};

/**
 * The solid border an upload thumbnail gets on hover, as the variation cards
 * have: drawn over the dashed border of the `DropZone` it is a child of —
 * which is 2px wide, outside the zone's padding box — with `borderRadius`.
 * Not inside the upload button, which clips what it holds.
 */
export const UploadHoverBorder = ({ isHovered, borderRadius }) => (
  <div
    aria-hidden
    className={style({
      position: "absolute",
      outlineWidth: 2,
      outlineStyle: "solid",
      outlineColor: { default: "transparent", isHovered: "gray-600" },
      outlineOffset: -2,
      pointerEvents: "none",
      transition: "[all]",
      transitionDuration: 300,
      willChange: "[outlineColor]",
    })({ isHovered })}
    style={{ inset: -2, borderRadius }}
  />
);

UploadHoverBorder.propTypes = {
  isHovered: PropTypes.bool,
  borderRadius: PropTypes.string,
};

/**
 * An image template asset the user fills by dropping or picking a file.
 *
 * `aspectRatio` (a CSS `aspect-ratio`) overrides the preview's default ratio,
 * and `isFluid` fills the container's width rather than a fixed one. The
 * `description` follows the preview. With `isCaptioned` the label still heads
 * the preview, but the `contextualHelp` and `actions` move below it, to the
 * row of the description (see `ThumbnailCaption`); the `children` come last.
 * Otherwise the `actions` sit at the end of the label's row.
 */
export const ImageAsset = ({
  label,
  description,
  name,
  isFilled,
  contextualHelp,
  filePath,
  src,
  isLoading,
  isDisabled = false,
  onDrop,
  getImageDragOperation: getDropOperation = getImageDragOperation,
  aspectRatio,
  isFluid = false,
  isCaptioned = false,
  actions,
  children,
  styles = "",
}) => {
  const [isHovered, setHovered] = useState(false);

  const dropZone = (
    <DropZone
      isDisabled={isDisabled}
      isFilled={isFilled}
      getDropOperation={getDropOperation}
      onDrop={(event) => onDrop(event.items, name)}
      UNSAFE_style={IMAGE_DROP_ZONE_STYLE}
    >
      <FileTrigger
        isDisabled={isDisabled}
        acceptedFileTypes="image/*"
        onSelect={(files) => onDrop(files, name)}
      >
        <AriaButton
          isDisabled={isDisabled}
          className={style({
            boxSizing: "border-box",
            position: "relative",
            size: "full",
            padding: 0,
            borderWidth: 0,
            position: "relative",
            backgroundColor: "transparent",
            overflow: "hidden",
            // transition: "colors",
          })}
          onHoverChange={setHovered}
        >
          {() => (
            <>
              {isFilled ? (
                <Card
                  variant="quiet"
                  styles={style({
                    minWidth: 0,
                    width: "auto",
                  })}
                >
                  <CardPreview>
                    <S3Image
                      filePath={filePath}
                      src={src || ""}
                      alt={name || ""}
                      styles={style({
                        objectFit: "contain",
                        aspectRatio: "30/19",
                        size: "full",
                        opacity: {
                          isLoading: 0.25,
                          default: 1,
                        },
                      })({
                        isLoading,
                      })}
                      UNSAFE_style={aspectRatio ? { aspectRatio } : undefined}
                    />
                    {!!isLoading && (
                      <ProgressCircle
                        aria-label="Loading"
                        isIndeterminate
                        staticColor="auto"
                        styles={style({
                          position: "absolute",
                          top: "50%",
                          insetStart: "50%",
                        })}
                        UNSAFE_className={style({
                          transform: "translate(-50%, -50%)",
                        })}
                      />
                    )}
                  </CardPreview>
                </Card>
              ) : (
                <div
                  className={style({
                    size: "full",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    aspectRatio: "16 / 9",
                  })}
                  style={aspectRatio ? { aspectRatio } : undefined}
                >
                  {!isLoading ? (
                    <>
                      <ImageIcon
                        styles={style({
                          size: {
                            default: 48,
                            md: 32,
                            "2xl": 48,
                          },
                        })}
                      />
                      <Heading
                        styles={style({
                          font: "title-sm",
                          color: "neutral-subdued",
                          margin: 0,
                        })}
                      >
                        Drag and drop an image
                      </Heading>
                      <Content
                        styles={style({
                          font: "ui-sm",
                          color: "neutral-subdued",
                        })}
                      >
                        or select a file
                      </Content>
                    </>
                  ) : (
                    <ProgressCircle
                      aria-label="Loading"
                      isIndeterminate
                      staticColor="auto"
                      styles={style({
                        position: "absolute",
                        top: "50%",
                        insetStart: "50%",
                      })}
                      UNSAFE_className={style({
                        transform: "translate(-50%, -50%)",
                      })}
                    />
                  )}
                </div>
              )}

              <UploadHoverOverlay isHovered={isHovered} />
            </>
          )}
        </AriaButton>
      </FileTrigger>
      <UploadHoverBorder
        isHovered={isHovered}
        borderRadius={IMAGE_DROP_ZONE_STYLE.borderRadius}
      />
    </DropZone>
  );

  return (
    <div
      key={name}
      className={
        style({
          display: "flex",
          flexDirection: "column",
          width: { default: 240, isFluid: "full" },
        })({ isFluid }) + styles
      }
    >
      {!!label && (
        // Only a heading: a `LabeledValue` cannot contain the drop zone's
        // file input.
        <LabeledValue
          label={label}
          value=""
          contextualHelp={isCaptioned ? undefined : contextualHelp}
        />
      )}
      {dropZone}
      <ThumbnailCaption
        description={description}
        contextualHelp={isCaptioned ? contextualHelp : undefined}
        actions={actions}
      />
      {!!children && <div className={style({ marginTop: 16 })}>{children}</div>}
    </div>
  );
};

ImageAsset.propTypes = {
  isDisabled: PropTypes.bool,
  label: PropTypes.string,
  description: PropTypes.string,
  name: PropTypes.string.isRequired,
  filePath: PropTypes.string,
  src: PropTypes.string,
  isFilled: PropTypes.bool,
  isLoading: PropTypes.bool,
  contextualHelp: PropTypes.node,
  onDrop: PropTypes.func.isRequired,
  getImageDragOperation: PropTypes.func,
  aspectRatio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isFluid: PropTypes.bool,
  isCaptioned: PropTypes.bool,
  actions: PropTypes.node,
  children: PropTypes.node,
  styles: PropTypes.string,
};
